import styled from '@emotion/styled'
import {LoadingButton} from '@mui/lab'
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import {RHFTextField} from '../../common/hook-form'
import RHFPincodeAddressField from '../../common/hook-form/RHFPincodeAddressField'

export const COLORS = {
  primary: '#fff',
  primaryGradient: 'linear-gradient(135deg, #3783e7, #1565c0)',
  white: '#FFF',
  gray: 'rgba(255,255,255,0.15)',
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  whiteTransparent: 'rgba(255, 255, 255, 0.15)',
  whiteTransparentHover: 'rgba(255, 255, 255, 0.25)',
  whiteTransparentLight: 'rgba(255,255,255,0.08)',
  shadow: 'rgba(0, 0, 0, 0.3)',
  // Form fields on white card — keep dark text
  fieldText: '#212B36',
  fieldBorder: 'rgba(0,0,0,0.23)',
  cardBg: '#ffffff',
}

const GRADIENTS = {
  primary: COLORS.primaryGradient,
  text: 'linear-gradient(90deg, #3783e7, #00d4ff)',
  border: `linear-gradient(white, white) padding-box, ${COLORS.primaryGradient} border-box`,
  borderHover: `linear-gradient(${COLORS.whiteTransparentLight}, ${COLORS.whiteTransparentLight}) padding-box, ${COLORS.primaryGradient} border-box`,
}

const BORDER_RADIUS = {
  small: '8px',
  medium: '12px',
  large: '24px',
  xlarge: '32px', // theme.spacing(4)
}

const SHADOWS = {
  text: '0 2px 8px rgba(0,0,0,0.4)',
  card: '0 16px 48px rgba(0, 0, 0, 0.25)',
}

const FLEX_LAYOUTS = {
  center: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  centerColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  spaceBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}

const BUTTON_BASE = {
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.85rem',
}

const BUTTON_BASE_SMALL = {
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.75rem',
}

const ROUNDED_CONTAINER_BASE = {
  borderRadius: BORDER_RADIUS.medium,
  borderTopLeftRadius: '0px',
  borderTopRightRadius: '0px',
  marginTop: -1,
}

const SCROLLBAR_STYLES = {
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: COLORS.whiteTransparent,
    borderRadius: '3px',
    '&:hover': {
      background: COLORS.whiteTransparentHover,
    },
  },
}

// MUI Styled Components
export const MainContainer = styled(Box)(({theme}) => ({
  minHeight: '100vh',
  background: COLORS.background,
  display: 'flex',
  flexDirection: 'column',
}))

export const ContentContainer = styled(Container)(({theme}) => ({
  flex: 1,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  maxWidth: '90% !important',
}))

export const GridContainer = styled(Grid)(({theme}) => ({
  minHeight: 'calc(100vh - 200px)',
}))

export const LeftGrid = styled(Grid)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    paddingRight: theme.spacing(3),
  },
}))

export const LogoContainer = styled(Box)(({theme}) => ({
  marginBottom: theme.spacing(4),
}))

export const Logo = styled('img')(({theme}) => ({
  height: '80px',
}))

export const MainHeading = styled(Typography)(({theme}) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(3),
  fontSize: '2rem',
  lineHeight: 1.2,
  textShadow: SHADOWS.text,
  [theme.breakpoints.up('md')]: {
    fontSize: '3rem',
  },
}))

export const HighlightText = styled(Box)(({theme}) => ({
  background: GRADIENTS.text,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}))

export const Subtitle = styled(Typography)(({theme}) => ({
  marginBottom: theme.spacing(1),
  fontWeight: 400,
  fontSize: '1.25rem',
  lineHeight: 1.6,
}))

export const FeaturesList = styled(List)(({theme}) => ({
  marginBottom: theme.spacing(4),
}))

export const FeatureItem = styled(ListItem)(({theme}) => ({
  paddingLeft: 0,
  paddingRight: 0,
}))

export const FeatureIcon = styled(ListItemIcon)(({theme}) => ({
  minWidth: 50,
}))

export const FeatureText = styled(ListItemText)(({theme}) => ({
  '& .MuiListItemText-primary': {
    color: 'rgba(255,255,255,0.85)',
    fontSize: '1rem',
    fontWeight: 500,
  },
}))

export const ButtonContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'space-between',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}))

export const PrimaryButton = styled(LoadingButton)(({theme}) => ({
  background: GRADIENTS.primary,
  color: COLORS.white,
  padding: `${theme.spacing(1.5)} ${theme.spacing(4)}`,
  borderRadius: theme.spacing(1),
  ...BUTTON_BASE,

  [theme.breakpoints.down('lg')]: {
    ...BUTTON_BASE_SMALL,
  },

  '&:hover': {
    backgroundColor: '#FFC107',
    color: '#1a1a1a',
  },
}))

export const SecondaryButton = styled(LoadingButton)(({theme}) => ({
  position: 'relative',
  border: '2px solid transparent',
  borderRadius: theme.spacing(1),
  background: GRADIENTS.border,
  color: COLORS.fieldText,
  padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  ...BUTTON_BASE,
  transition: 'all 0.3s ease',

  [theme.breakpoints.down('lg')]: {
    ...BUTTON_BASE_SMALL,
  },

  '&:hover': {
    background: GRADIENTS.borderHover,
    color: COLORS.white,
  },
}))

export const RightGrid = styled(Grid)(({theme}) => ({
  ...FLEX_LAYOUTS.center,
}))

export const LoginCard = styled(Card)(({theme}) => ({
  width: '100%',
  minWidth: 400,
  maxWidth: '80%',
  minHeight: 580,
  borderRadius: '24px',
  backgroundColor: COLORS.cardBg,
  boxShadow: SHADOWS.card,
}))

export const LoginCardContent = styled(CardContent)(({theme}) => ({
  padding: theme.spacing(4),
}))

export const StyledLoadingButton = styled(PrimaryButton)({
  '&.Mui-disabled': {
    opacity: 0.5,
    color: COLORS.white,
  },
})

export const HeaderContainer = styled(Box)({
  textAlign: 'center',
  marginBottom: '1.5rem',
})

export const WelcomeTitle = styled(Typography)(({theme}) => ({
  fontWeight: 700,
  color: COLORS.fieldText,
  marginBottom: '0.25rem',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.25rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.35rem',
  },
}))

export const SubTitle = styled(Typography)({
  fontSize: '0.8rem',
  color: '#6b7280',
})

// Form fields sit on a WHITE card — all colors must be dark-on-white
const baseFieldStyles = {
  '& input': {
    color: `${COLORS.fieldText} !important`,
    fontSize: '0.82rem',
  },
  '& .MuiFormHelperText-root': {
    color: '#d32f2f !important',
  },
  '& label': {
    color: `#6b7280 !important`,
    fontSize: '0.8rem',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: BORDER_RADIUS.small,
    backgroundColor: '#f9fafb',
    '& fieldset': {
      borderColor: `${COLORS.fieldBorder} !important`,
    },
    '&:hover fieldset': {
      borderColor: '#3783e7 !important',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3783e7 !important',
      borderWidth: '2px',
    },
  },
  '& input::placeholder': {
    color: '#9ca3af',
    opacity: 1,
  },
}

export const StyledTextField = styled(RHFTextField)(baseFieldStyles)
export const StyledTextFieldClient = styled(styled(TextField)(baseFieldStyles))({
  '& .MuiOutlinedInput-root': {
    borderRadius: BORDER_RADIUS.medium,
    borderBottomLeftRadius: '0px',
    borderBottomRightRadius: '0px',
  },
})

export const StyledPincodeField = styled(RHFPincodeAddressField)(baseFieldStyles)

export const PasswordToggleButton = styled(IconButton)({
  color: '#6b7280',
})

export const ForgotPasswordButton = styled(Button)({
  color: '#3783e7 !important',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.78rem',
})

export const StyledDivider = styled(Divider)({
  '& p': {
    color: '#9ca3af !important',
    fontSize: '0.8rem',
  },
  marginTop: '1rem',
  marginBottom: '1rem',

  '&:before': {
    borderTopColor: '#e5e7eb !important',
  },
  '&:after': {
    borderTopColor: '#e5e7eb !important',
  },
})

export const DividerText = styled(Typography)({
  paddingLeft: '1rem',
  paddingRight: '1rem',
  color: '#9ca3af',
})

export const GoogleSignInButton = styled(Button)({
  width: '100%',
  height: 42,
  borderRadius: 8,
  fontWeight: 600,
  fontSize: '0.78rem',
  textTransform: 'none',
  background: '#fff',
  color: COLORS.fieldText,
  border: '1.5px solid #e5e7eb',
  boxShadow: 'none',
  '&:hover': {
    background: '#f9fafb',
    borderColor: '#3783e7',
    boxShadow: 'none',
  },
})

export const MSALSignInButton = styled(Button)({
  width: '100%',
  height: 42,
  borderRadius: 8,
  fontWeight: 600,
  fontSize: '0.78rem',
  textTransform: 'none',
  background: '#fff',
  color: COLORS.fieldText,
  border: '1.5px solid #e5e7eb',
  boxShadow: 'none',
  '&:hover': {
    background: '#f9fafb',
    borderColor: '#3783e7',
    boxShadow: 'none',
  },
})

export const IconContainer = styled(Box)({
  marginRight: '0.5rem',
  ...FLEX_LAYOUTS.center,
})

export const StyledIcon = styled('div')({
  color: '#6b7280',
  fontSize: '20px',
  ...FLEX_LAYOUTS.center,
})

// Add these new styled components for better merchant selection UI
export const MerchantContainer = styled(Box)({
  maxHeight: '400px',
  overflowY: 'auto',
  border: `1px solid ${COLORS.fieldBorder}`,
  padding: '8px',
  ...ROUNDED_CONTAINER_BASE,
  ...SCROLLBAR_STYLES,
})

export const StyledMenuItem = styled(Box)({
  ...FLEX_LAYOUTS.spaceBetween,
  padding: '12px 16px',
  margin: '4px 0',
  borderRadius: BORDER_RADIUS.small,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  border: '1px solid transparent',
  '&:hover': {
    transform: 'translateY(-1px)',
    border: `1px solid ${COLORS.whiteTransparent}`,
    boxShadow: SHADOWS.card,
  },
})

export const MerchantName = styled(Typography)({
  color: COLORS.fieldText,
  fontWeight: 500,
  fontSize: '0.82rem',
})

export const SearchContainer = styled(Box)({
  position: 'relative',
  marginBottom: '0px', // Changed from '20px' to '0px'
})

export const LogoutButton = styled(Button)({
  color: COLORS.fieldText,
  textTransform: 'none',
  fontWeight: 500,
  padding: '6px 16px',
  outline: 'none',
})

export const EmptyStateContainer = styled(Box)({
  textAlign: 'center',
  padding: '40px 20px',
  color: COLORS.fieldText,
  border: `1px solid ${COLORS.fieldBorder}`,
  ...ROUNDED_CONTAINER_BASE,
})

export const ArrowIcon = styled('div')({
  color: 'rgba(255, 255, 255, 0.7)',
  transition: 'all 0.2s ease',
  ...FLEX_LAYOUTS.center,
})
